import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  useTheme,
} from '@mui/material';
import { AttachFile as AttachIcon } from '@mui/icons-material';
import { FileDropZoneProps } from './types';
import { formatFileSize } from './utils';

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  maxFiles,
  maxFileSize,
  acceptedTypes,
  disabled,
  onFilesSelect,
  error,
}) => {
  const theme = useTheme();
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    onFilesSelect(droppedFiles);
  }, [disabled, onFilesSelect]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    onFilesSelect(selectedFiles);
    e.target.value = ''; // リセット
  }, [onFilesSelect]);

  return (
    <Box>
      {/* ファイル選択・ドロップエリア */}
      <Paper
        variant="outlined"
        sx={{
          border: `2px dashed ${
            dragOver ? theme.palette.primary.main : theme.palette.grey[300]
          }`,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(2),
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: dragOver ? theme.palette.primary.light + '10' : 'transparent',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.5 : 1,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && document.getElementById('file-input')?.click()}
      >
        <input
          id="file-input"
          type="file"
          multiple
          style={{ display: 'none' }}
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          disabled={disabled}
        />
        
        <AttachIcon sx={{ fontSize: 32, color: theme.palette.grey[500], mb: 1 }} />
        
        <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
          ファイルをドラッグ&ドロップするか、クリックして選択
        </Typography>
        
        <Typography variant="caption" sx={{ color: theme.palette.grey[500] }}>
          {formatFileSize(maxFileSize)}まで、最大{maxFiles}個
        </Typography>
      </Paper>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}; 